<template>
  <div class="user-cdds app__block">
    <h1>{{ 'user.businesses-tab' | globalize }}</h1>
    <div
      class="app-list__filters business-list__filters"
      v-if="showFilters">
      <a-row
        type="flex"
        :gutter="filterGutter"
      >
        <a-col span="2">
          <input-field
            v-model.trim="filters.userId"
            :label="'business-list.lbl-user-id' | globalize"
          />
        </a-col>
        <a-col span="6">
          <input-field
            v-model.trim="filters.businessName"
            :label="'business-list.lbl-business-name' | globalize"
          />
        </a-col>
        <a-col span="6">
          <input-field
            v-model.trim="filters.address"
            :label="'business-list.lbl-address' | globalize"
          />
        </a-col>
        <a-col span="10">
          <m-select
            :placeholder="'business-list.lbl-status' | globalize"
            :options="statuses"
            :value="filters.status"
            :disabled="isPending"
            :on-change-values="onChangeStatuses"
          />
        </a-col>
      </a-row>
    </div>

    <div class="app-list business-list__wrp">
      <div class="app-list__header">
        <span class="app-list__cell app-list__cell--left">
          {{ "business-list.lbl-id" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--left">
          {{ "business-list.lbl-user-id" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--center">
          {{ "business-list.lbl-address" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--center">
          {{ "business-list.lbl-status" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--right">
          {{ "business-list.lbl-name" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--right">
          {{ "business-list.lbl-created" | globalize }}
        </span>
      </div>
      <template v-if="isLoaded && businessList.length && !isFailed">
        <a
          class="app-list__li"
          v-ripple
          v-for="item in businessList"
          :href="`/businesses/${item.id}`"
          :key="`businesses-${item.id}`"
          @click.prevent="redirectToDetails(item.id)"
        >
          <span
            class="app-list__cell app-list__cell--left"
            :title="item.id"
          >
            {{ item.id }}
          </span>

          <span
            class="app-list__cell app-list__cell--left"
            :title="item.userId"
          >
            <copyable :copy-text="item.userId.toString()">
              {{ item.userId }}
            </copyable>
          </span>

          <span
            class="app-list__cell app-list__cell--left"
            :title="item.address"
          >
            <copyable
              class="app-key-value__value"
              :copy-text="item.address"
              v-if="item.address">
              <a
                target="_blank"
                :href="etherscanUrl('address', item.address)"
                @click="e => redirectToUrl(e)"
                :title="item.address"
              >
                {{ shortener(item.address) }}
              </a>
            </copyable>
            <span v-else>
              {{ item.address }}
            </span>
          </span>

          <span
            class="app-list__cell app-list__cell--center"
            :title="item.statusAsString"
          >
            {{ item.statusAsString }}
          </span>

          <span
            class="app-list__cell app-list__cell--center"
            :title="item.businessName"
          >
            <template v-if="item.businessName">
              <copyable :copy-text="item.businessName">
                {{ item.businessName }}
              </copyable>
            </template>
          </span>

          <span
            class="app-list__cell app-list__cell--right"
            :title="item.createdAt"
          >
            {{ item.createdAt | formatDateDMYT }}
          </span>
        </a>
      </template>
      <template v-else>
        <div class="app-list__li-like">
          <template v-if="isLoaded && !businessList.length">
            <p>
              {{ "verifications-list.nothing-here-yet" | globalize }}
            </p>
          </template>

          <template v-else-if="isFailed">
            <p>
              {{ "verifications-list.default-error-message" | globalize }}
            </p>
          </template>

          <template v-else>
            <p>
              {{ "verifications-list.loading-message" | globalize }}
            </p>
          </template>
        </div>
      </template>
    </div>
    <collection-loader
      class="app__more-btn-wrp"
      :first-page-loader="getBusinesses"
      @first-page-load="setList"
      @next-page-load="concatList"
      ref="collectionLoaderBtn"
    />
  </div>
</template>

<script>
import ARow from 'ant-design-vue/lib/row'
import ACol from 'ant-design-vue/lib/col'
import { api } from '@/api'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { BusinessRecord, BUSINESS_STATUSES } from '@/js/records/entities/business.record'
import CollectionLoader from '@/vue/common/CollectionLoader'
import Copyable from '@/vue/common/copyable/Copyable'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'
import InputField from '@/vue/fields/InputField'
import MSelect from '@/vue/fields/MSelect'
import { vueRoutes } from '@/vue-router/routes'
import { etherscanUrl, polygonscanUrl } from '@/js/helpers/urls'
import { shortener } from '@/js/helpers/string'

const defaultFilters = {
  userId: '',
  status: [],
  businessName: '',
  address: '',
}

export default {
  name: 'business-list',
  components: {
    CollectionLoader,
    Copyable,
    InputField,
    MSelect,
    ARow,
    ACol,
  },
  data () {
    return {
      showFilters: true,
      isLoaded: false,
      isFailed: false,
      isPending: false,
      filterGutter: { xs: 1, sm: 1, md: 2, lg: 2 },
      statuses: BUSINESS_STATUSES,
      businessList: [],
      filters: defaultFilters,
    }
  },
  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
    '$route' () {
      this.filters = queryToFilters(defaultFilters)
      return this.getBusinesses()
    },
  },
  async created () {
    this.filters = queryToFilters(defaultFilters)
    await this.getBusinesses()
  },
  methods: {
    async getBusinesses () {
      this.isLoaded = false
      this.isFailed = false
      this.isPending = true
      let response = {}
      try {
        const statuses = this.filters.status.join(',')
        const query = {
          filter: clearObject({
            user_id: this.filters.userId,
            address: this.filters.address,
            status: statuses,
            business_name: this.filters.businessName,
          }),
        }

        response = await api.getWithSignature('/users/businesses', query)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }

      return response
    },

    redirectToDetails (id) {
      this.$router.push({
        name: vueRoutes.businessDetails.name,
        params: { id },
      })
    },

    setList (data) {
      this.businessList = data.map((i) => new BusinessRecord(i))
    },

    concatList (data) {
      this.businessList = this.businessList.concat(
        data.map((i) => new BusinessRecord(i))
      )
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    onChangeStatuses (value) {
      this.filters.status = value
    },

    redirectToUrl (evt) {
      evt.stopPropagation()
    },

    etherscanUrl,
    polygonscanUrl,
    shortener,
  },
}
</script>

<style lang="scss" scoped>
  @import '~ant-design-vue/lib/grid/style/index.css';

  .business-list__wrp {
    padding-top: 4rem;
  }

  .business-list__field {
    min-width: 9.2rem;
  }

  .app-list-filters {
    display: inherit;
  }
</style>
